import { useEffect, useRef, useState } from 'react';

import { cn } from '@utils/cn';

import { Button } from '@ui-component-library/components/Button';
import { Icon } from '@ui-component-library/components/Icon';
import { Text } from '@ui-component-library/components/Text';
import { useDebounce } from '@ui-component-library/hooks/useDebounce';

import {
  clearButtonClasses,
  contentClasses,
  inputClasses,
  labelClasses,
  prefixClasses,
  statusIconClasses,
  suffixClasses,
  wrapperClasses
} from './Input.styles';
import type { InputProps } from './Input.types';

function Input({
  name,
  className,
  type,
  label,
  variant = 'default',
  // required,
  value,
  isValid = undefined,
  prefix,
  suffix,
  placeholder,
  disabled = false,
  onChange,
  debounceDelay = 300,
  onFocus = () => {},
  onBlur = () => {},
  ...props
}: InputProps) {
  if (variant === 'default') {
    throw new Error('Variant "default" is not supported. Please use "fluid" instead.');
  }

  const hasValue = (v: unknown) => v !== null && v !== undefined && v !== '';

  const [inputValue, setInputValue] = useState(value ?? '');
  const [isFocused, setIsFocused] = useState(hasValue(value));
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedInputValue = useDebounce(inputValue, debounceDelay);

  useEffect(() => {
    onChange(debouncedInputValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValue]);

  useEffect(() => {
    setInputValue(value ?? '');
    if (hasValue(value)) {
      setIsFocused(true);
    }
  }, [value]);

  const handleChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(currentTarget.value);
  };

  const handleFocus = () => {
    setIsFocused(true);
    onFocus();
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!hasValue(inputValue) && !wrapperRef.current?.contains(e.relatedTarget)) {
      setIsFocused(false);
    }
    onBlur();
  };

  const clearInput = () => {
    setInputValue('');
  };

  const shouldShowValidation = isValid !== undefined;
  const showSuccessIcon = shouldShowValidation && isValid === true;
  const showErrorIcon = shouldShowValidation && isValid === false;

  return (
    <div
      ref={wrapperRef}
      className={cn(
        wrapperClasses({
          validationStatus: isValid,
          disabled: disabled
        }),
        className
      )}
      onClick={() => {
        inputRef.current?.focus();
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {prefix && <div className={cn(prefixClasses({ isFocused }))}>{prefix}</div>}

      <div className={cn(contentClasses({}))}>
        <label htmlFor={name} className={cn(labelClasses({ isFocused }))}>
          {label}
        </label>

        <input
          ref={inputRef}
          type={type}
          data-slot="input"
          name={name}
          placeholder={placeholder ?? ''}
          value={inputValue}
          className={cn(inputClasses({ isFocused }), className)}
          onChange={handleChange}
          {...props}
        />

        {hasValue(inputValue) && (
          <Button
            variant="tertiary"
            size="icon"
            className={cn(clearButtonClasses({ hasInputValue: hasValue(inputValue), isFocused }))}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={clearInput}
          >
            <Icon name="CrossFilled" size="large" />
          </Button>
        )}

        {showSuccessIcon && (
          <Icon
            name="CheckFilled"
            className={cn(statusIconClasses({ isFocused, status: 'success' }))}
            size="large"
          />
        )}

        {showErrorIcon && (
          <Icon
            name="ExclamationFilled"
            className={cn(statusIconClasses({ isFocused, status: 'error' }))}
            size="large"
          />
        )}

        <Text variant="labelMediumRegular" className={cn(suffixClasses({ isFocused }))}>
          {suffix}
        </Text>
      </div>
    </div>
  );
}

export { Input };
